import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../../config/firebase';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './recentTodos.css';

const RecentTodos = () => {
    const [myTodos, setMyTodos] = useState([]);
    const [myPartnerTodos, setMyPartnerTodos] = useState([]);
    const [expiredTodosCount, setExpiredTodosCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const todosTrackingRef = useRef(new Set());
    const unsubscribersRef = useRef([]);

    useEffect(() => {
        const setupRealTimeListeners = async () => {
            if (!auth.currentUser) {
                setLoading(false);
                return;
            }

            try {
                // Clean up any existing listeners
                unsubscribersRef.current.forEach(unsub => unsub());
                unsubscribersRef.current = [];

                const userId = auth.currentUser.uid;

                // Get user info to find partner
                const userInfoDoc = await getDoc(doc(db, "UserInformation", userId));
                let partnerId = null;

                if (userInfoDoc.exists()) {
                    const userData = userInfoDoc.data();
                    partnerId = userData.inRelationWith;
                }

                // Set up real-time listener for user's todos
                const userTodosRef = collection(db, "events", userId, "userTodos");
                const userUnsubscribe = onSnapshot(userTodosRef, (snapshot) => {
                    const userTodos = [];
                    snapshot.forEach(doc => {
                        const data = doc.data();
                        const dueDate = data.date ? new Date(data.date) : null;
                        userTodos.push({
                            ...data,
                            id: doc.id,
                            dueDate: dueDate,
                            isCurrentUser: true
                        });
                    });

                    processUserTodos(userTodos);
                }, (error) => {
                    console.error("Error listening to user todos:", error);
                    setError("Failed to load todos. Please refresh the page.");
                    setLoading(false);
                });
                
                unsubscribersRef.current.push(userUnsubscribe);

                // Set up real-time listener for partner's todos if partner exists
                if (partnerId) {
                    const partnerTodosRef = collection(db, "events", partnerId, "userTodos");
                    const partnerUnsubscribe = onSnapshot(partnerTodosRef, (snapshot) => {
                        const partnerTodos = [];
                        snapshot.forEach(doc => {
                            const data = doc.data();
                            const dueDate = data.date ? new Date(data.date) : null;
                            partnerTodos.push({
                                ...data,
                                id: doc.id,
                                dueDate: dueDate,
                                isCurrentUser: false
                            });
                        });

                        processPartnerTodos(partnerTodos);
                    }, (error) => {
                        console.error("Error listening to partner todos:", error);
                        setError("Failed to load partner todos. Please refresh the page.");
                    });
                    
                    unsubscribersRef.current.push(partnerUnsubscribe);
                }

            } catch (error) {
                console.error("Error setting up real-time listeners:", error);
                setError("Failed to connect to server. Please refresh the page.");
                setLoading(false);
            }
        };

        setupRealTimeListeners();

        // Clean up on unmount
        return () => {
            unsubscribersRef.current.forEach(unsub => unsub());
        };
    }, []);

    const navigateToTodoList = () => {
        navigate('/dashboard/organization/todo-list');
    };

    // Process user todos and update state
    const processUserTodos = (userTodos) => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const expiredTodos = userTodos.filter(todo =>
            todo.dueDate && new Date(todo.dueDate) < now && !todo.status
        );
        setExpiredTodosCount(expiredTodos.length);

        const validTodos = userTodos.filter(todo =>
            !todo.dueDate || new Date(todo.dueDate) >= now || todo.status
        );

        // Create a map of all todos by ID for fast lookup
        const todoMap = new Map(userTodos.map(todo => [todo.id, todo]));
        todosTrackingRef.current = todoMap;

        // Filter todos for "My Todos" - todos created by current user and either assigned to themselves or not assigned
        const myTodosList = validTodos.filter(todo =>
            (todo.assigneeId === auth.currentUser.uid || (!todo.assigneeId && todo.isCurrentUser))
        );

        // Filter todos for "My Partner's Todos" - todos assigned to the partner
        const myPartnerTodosList = validTodos.filter(todo =>
            todo.assigneeId && todo.assigneeId !== auth.currentUser.uid &&
            // Exclude unassigned todos created by the current user
            !(todo.isCurrentUser && !todo.assigneeId)
        );

        // Update state with new filtered lists
        setMyTodos(myTodosList.slice(0, 4));
        setMyPartnerTodos(prevPartnerTodos => {
            // Keep partner todos that aren't from user, add user's partner-assigned todos
            const userPartnerTodos = myPartnerTodosList.slice(0, 4);
            const existingPartnerTodos = prevPartnerTodos.filter(todo => !todo.isCurrentUser);
            return [...existingPartnerTodos, ...userPartnerTodos].slice(0, 4);
        });
        setLoading(false);
    };

    // Process partner todos and update state
    const processPartnerTodos = (partnerTodos) => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        // Only show non-expired todos
        const validPartnerTodos = partnerTodos.filter(todo =>
            !todo.dueDate || new Date(todo.dueDate) >= now || todo.status
        );

        // Filter partner todos that should appear in "My Todos" (assigned to current user)
        const partnerTodosForMe = validPartnerTodos.filter(todo =>
            todo.assigneeId === auth.currentUser.uid
        );

        // Filter partner todos that should appear in "My Partner's Todos" (assigned to the partner or unassigned by partner)
        const partnerTodosForPartner = validPartnerTodos.filter(todo =>
            todo.assigneeId !== auth.currentUser.uid &&
            // Include unassigned todos created by the partner
            !todo.isCurrentUser
        );

        // Update myTodos - add partner todos assigned to me
        setMyTodos(prevTodos => {
            const userTodos = prevTodos.filter(todo => todo.isCurrentUser);
            return [...userTodos, ...partnerTodosForMe].slice(0, 4);
        });

        // Update myPartnerTodos - add partner todos for partner
        setMyPartnerTodos(prevTodos => {
            const userPartnerTodos = prevTodos.filter(todo => todo.isCurrentUser);
            return [...userPartnerTodos, ...partnerTodosForPartner].slice(0, 4);
        });
    };

    // Helper function to render todo items
    const renderTodoItems = (todos) => {
        return todos.map(todo => {
            const statusClass = todo.status ? 'completed' : 'pending';
            const priorityClass = `priority-${todo.priority || 'medium'}`;

            return (
                <li key={todo.id} onClick={navigateToTodoList} className={`todo-item ${statusClass} ${priorityClass}`}>
                    <div className="todo-item-content">
                        <div className="todo-details">
                            <h4 className={todo.status ? 'line-through' : ''}>{todo.todo}</h4>
                            <div className="todo-meta">
                                {todo.dueDate && (
                                    <span className="due-date">
                                        <i className="pi pi-calendar"></i> {todo.dueDate instanceof Date ? todo.dueDate.toLocaleDateString() : new Date(todo.dueDate).toLocaleDateString()}
                                    </span>
                                )}
                                {todo.category && (
                                    <span className="category">
                                        {todo.category}
                                    </span>
                                )}
                                <span className={`priority ${priorityClass}`}>
                                    {todo.priority || 'medium'}
                                </span>
                            </div>
                        </div>
                    </div>
                </li>
            );
        });
    };

    return (
        <div className="dashboard-panel recent-todos-panel">
            <div className="panel-header">
                <h3 className="panel-title">Recent Todos</h3>
            </div>

            {loading ? (
                <div className="todo-loading">Loading recent todos...</div>
            ) : (
                <div className="todos-container">
                    {myTodos.length === 0 && myPartnerTodos.length === 0 ? (
                        <div className="no-todos">
                            <p>No recent todos found</p>
                            {error && <p className="error-message">{error}</p>}
                        </div>
                    ) : (
                        <>
                            {myTodos.length > 0 && (
                                <div className="todos-section">
                                    <span className="panel-subtitle">My Todos</span>
                                    <div style={{ marginTop: '1rem' }}>
                                        <ul className="recent-todos-list">
                                            {renderTodoItems(myTodos)}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {myPartnerTodos.length > 0 && (
                                <div className="todos-section">
                                    <span className="panel-subtitle">My Partner's Todos</span>
                                    <div style={{ marginTop: '1rem' }}>
                                        <ul className="recent-todos-list">
                                            {renderTodoItems(myPartnerTodos)}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {expiredTodosCount > 0 && (
                <div className="expired-todos-notification" onClick={navigateToTodoList}>
                    <i className="pi pi-exclamation-triangle"></i>
                    <span>{expiredTodosCount} expired todo{expiredTodosCount > 1 ? 's' : ''}</span>
                </div>
            )}
        </div>
    );
};

export default RecentTodos;